import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { api, ApiError, type BookmarkQuestion, type PracticeInfo, type StartOpts } from '../lib/api';
import { grad } from '../components/ui';

function sizeLabel(n: number, isMax: boolean) {
  if (isMax) return 'ALL';
  if (n <= 5) return 'QUICK';
  if (n <= 10) return 'SHORT';
  if (n <= 20) return 'MEDIUM';
  return 'LONG';
}

export function PracticeSetup() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const [info, setInfo] = useState<PracticeInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [size, setSize] = useState<number | null>(null);
  const [year, setYear] = useState<string>('all');
  const [starting, setStarting] = useState(false);
  const [bookmarks, setBookmarks] = useState<BookmarkQuestion[]>([]);
  const [bmBusy, setBmBusy] = useState<string | null>(null);

  const load = useCallback(() => {
    let cancelled = false;
    setLoading(true);
    api
      .practiceInfo(String(productId))
      .then((d) => {
        if (cancelled) return;
        setInfo(d);
        setSize((prev) => prev ?? d.sizes?.[0] ?? null);
        api
          .getBookmarks()
          .then((r) => !cancelled && setBookmarks(r.questions.filter((q) => q.productId === d.product.id)))
          .catch(() => {});
      })
      .catch(() => {})
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [productId]);

  useEffect(() => load(), [load]);

  async function start(opts: StartOpts) {
    if (starting) return;
    setStarting(true);
    try {
      const { sessionId } = await api.startSession(String(productId), opts);
      navigate(`/practice/session/${sessionId}`);
    } catch (e) {
      setStarting(false);
      alert(e instanceof ApiError ? e.message : 'Could not start the session.');
    }
  }

  async function removeBookmark(id: string) {
    setBmBusy(id);
    try {
      await api.toggleBookmark(id);
      setBookmarks((qs) => qs.filter((q) => q.id !== id));
    } catch {
      /* ignore */
    } finally {
      setBmBusy(null);
    }
  }

  if (loading || !info) return <div className="loading-screen"><div className="spinner" /></div>;

  const maxSize = info.sizes?.length ? Math.max(...info.sizes) : 0;
  const estMin = size ? Math.max(1, Math.round(size * 0.75)) : 0;

  const subline =
    info.kind === 'testseries'
      ? `${info.papers?.length ?? 0} mock papers`
      : info.kind === 'pyq'
        ? `${info.available} questions · ${info.years?.length ?? 0} years`
        : `${info.available} questions`;

  return (
    <div style={{ paddingTop: 20 }}>
      <div className="hero" style={{ ...grad(info.product.gradient), textAlign: 'center' }}>
        <div style={{ fontSize: 46 }}>{info.product.emoji}</div>
        <h1 style={{ fontSize: 22, fontWeight: 900, marginTop: 6 }}>{info.product.title}</h1>
        <p style={{ opacity: 0.9, marginTop: 4 }}>{subline}</p>
      </div>

      {!info.access ? (
        <div className="card col center" style={{ background: 'var(--accent-soft)', marginTop: 20, gap: 8, textAlign: 'center' }}>
          <div style={{ fontSize: 28 }}>🔒</div>
          <div style={{ fontWeight: 800 }}>Purchase to practise</div>
          <p className="muted">Buy this pack from the store to start attempting questions.</p>
        </div>
      ) : info.kind === 'testseries' ? (
        /* ---------- TEST SERIES: pick a mock paper ---------- */
        <section className="section">
          <h2 className="section-title">Mock papers</h2>
          <p className="muted" style={{ marginTop: 2, marginBottom: 14 }}>Timed, exam-style. Negative marking applies.</p>
          {(info.papers ?? []).map((p) => (
            <div key={p.paper} className="card row gap12" style={{ marginBottom: 12, alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{p.title}</div>
                <div className="muted" style={{ fontSize: 13, marginTop: 3 }}>
                  {p.count} Qs · ⏱️ {p.durationMin} min · +{p.marksCorrect}/{p.marksWrong} marking
                </div>
              </div>
              <button className="btn btn-primary btn-sm" disabled={starting} onClick={() => start({ paper: p.paper })}>
                Start →
              </button>
            </div>
          ))}
        </section>
      ) : (
        /* ---------- QUIZ / PYQ: (optional year) + size ---------- */
        <>
          {info.kind === 'pyq' && (info.years?.length ?? 0) > 0 && (
            <section className="section">
              <h2 className="section-title">Year</h2>
              <div className="row gap8 wrap" style={{ marginTop: 10 }}>
                <button className={`chip ${year === 'all' ? 'active' : ''}`} onClick={() => setYear('all')}>All years</button>
                {info.years!.map((y) => (
                  <button key={y} className={`chip ${year === y ? 'active' : ''}`} onClick={() => setYear(y)}>{y}</button>
                ))}
              </div>
            </section>
          )}

          <section className="section">
            <h2 className="section-title">Session size</h2>
            <p className="muted" style={{ marginTop: 2, marginBottom: 14 }}>How many questions this session?</p>
            <div className="row gap12 wrap">
              {(info.sizes ?? []).map((n) => {
                const active = size === n;
                return (
                  <button
                    key={n}
                    onClick={() => setSize(n)}
                    className="card center col"
                    style={{ width: 100, height: 90, border: active ? '1.5px solid var(--primary)' : '1.5px solid var(--border)', background: active ? 'var(--primary)' : 'var(--surface)', color: active ? '#fff' : 'var(--text)' }}
                  >
                    <div style={{ fontSize: 26, fontWeight: 900 }}>{n}</div>
                    <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: 0.5, opacity: active ? 0.9 : 0.6 }}>{sizeLabel(n, n === maxSize)}</div>
                  </button>
                );
              })}
            </div>
            <div className="chip" style={{ marginTop: 16 }}>⏱️ Estimated time: <b style={{ marginLeft: 4 }}>{estMin} min</b></div>
          </section>

          <button
            className="btn btn-primary btn-block"
            style={{ marginTop: 20 }}
            onClick={() => start({ size: size ?? undefined, year: info.kind === 'pyq' ? year : undefined })}
            disabled={!size || starting}
          >
            {starting ? 'Starting…' : `Start ${info.kind === 'pyq' ? 'PYQ' : 'Quiz'}  ·  ${size ?? 0} Qs →`}
          </button>
        </>
      )}

      {/* Past attempts */}
      {info.recent.length > 0 && (
        <section className="section">
          <h2 className="section-title" style={{ marginBottom: 12 }}>Past attempts</h2>
          {info.recent.map((r) => {
            const isTs = r.type === 'testseries';
            const good = isTs ? r.marks >= r.maxMarks * 0.5 : r.percent >= 60;
            const label = isTs ? `${r.marks}/${r.maxMarks} marks` : `${r.percent}%`;
            return (
              <button key={r.id} className="card row gap12" style={{ width: '100%', marginBottom: 10, textAlign: 'left' }} onClick={() => navigate(`/practice/session/${r.id}`)}>
                <span className="badge" style={{ background: (good ? 'var(--success)' : 'var(--accent)') + '20', color: good ? 'var(--success)' : 'var(--accent)' }}>{label}</span>
                <span style={{ flex: 1, fontWeight: 600 }}>
                  {isTs && r.paper ? `${r.paper} · ` : ''}Scored {r.score}/{r.total}
                </span>
                <span className="link">Review ›</span>
              </button>
            );
          })}
        </section>
      )}

      {/* Bookmarked questions for this product */}
      <section className="section">
        <h2 className="section-title" style={{ marginBottom: 12 }}>🔖 Bookmarked questions ({bookmarks.length})</h2>
        {bookmarks.length === 0 ? (
          <div className="card muted" style={{ textAlign: 'center' }}>
            No bookmarks yet. Tap the 🔖 on any question while solving or reviewing to save it here.
          </div>
        ) : (
          bookmarks.map((q, i) => (
            <div key={q.id} className="card" style={{ marginBottom: 12, borderLeft: '3px solid var(--primary)' }}>
              <div className="row" style={{ justifyContent: 'space-between', gap: 10, alignItems: 'flex-start' }}>
                <div style={{ fontWeight: 700, lineHeight: 1.4, flex: 1 }}>
                  {q.year ? <span className="badge" style={{ background: 'var(--primary-soft)', color: 'var(--primary)', marginRight: 6 }}>{q.year}{q.session ? ` · ${q.session}` : ''}</span> : null}
                  {i + 1}. {q.text}
                </div>
                <button onClick={() => removeBookmark(q.id)} disabled={bmBusy === q.id} title="Remove bookmark" style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer' }}>🔖</button>
              </div>
              <div className="col gap8" style={{ marginTop: 10 }}>
                {q.options.map((opt, oi) => {
                  const correct = oi === q.answer;
                  return (
                    <div key={oi} className="row" style={{ justifyContent: 'space-between', padding: '8px 10px', borderRadius: 8, background: correct ? 'var(--success-soft)' : 'var(--surface-alt)' }}>
                      <span style={{ fontWeight: correct ? 700 : 400, color: correct ? 'var(--text)' : 'var(--muted)' }}>{String.fromCharCode(65 + oi)}. {opt}</span>
                      {correct && <span style={{ color: 'var(--success)' }}>✓</span>}
                    </div>
                  );
                })}
              </div>
              {q.solution && <p className="muted" style={{ marginTop: 10, lineHeight: 1.5 }}><b>Solution: </b>{q.solution}</p>}
            </div>
          ))
        )}
      </section>
    </div>
  );
}
